import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { G, errMsg } from "./guestApi";

const EMPTY = { name: "", phone: "", email: "", birthday: "", anniversary: "",
  city: "", notes: "" };

export function GuestFormDialog({ open, onClose, session, center, contact, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [check, setCheck] = useState(null);
  const [confirmE164, setConfirmE164] = useState("");
  const [busy, setBusy] = useState(false);
  const editing = !!contact;

  useEffect(() => {
    if (!open) return;
    setF(contact ? { ...EMPTY, ...contact, phone: contact.phone_e164 } : EMPTY);
    setCheck(null); setConfirmE164("");
  }, [open, contact]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const runCheck = async () => {
    if (!f.phone?.trim()) return;
    try {
      const r = await api.post(`${G}/check-duplicate`, { token: session.token,
        center: contact?.center || center, phone: f.phone, exclude_id: contact?.id });
      setCheck(r.data);
      setConfirmE164(r.data.phone.suggestion || "");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { token: session.token, ...f,
        confirm_phone_e164: check?.phone?.status === "NEEDS_CONFIRMATION" ? confirmE164 : null };
      const r = editing
        ? await api.post(`${G}/update`, { ...body, id: contact.id })
        : await api.post(`${G}/create`, { ...body, center });
      toast.success(editing ? "Guest updated" : "Guest added");
      onSaved(r.data.contact); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "PHONE_NEEDS_CONFIRMATION") {
        setCheck({ phone: { status: "NEEDS_CONFIRMATION", suggestion: d.suggestion,
          issues: [d.message], alternatives: d.alternatives } });
        setConfirmE164(d.suggestion || "");
      }
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const ph = check?.phone;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="guest-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit guest" : `Add guest · ${center}`}</DialogTitle>
          <DialogDescription>Consent starts as Unknown. Change it from the consent button.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Guest name *</Label>
            <Input value={f.name} onChange={set("name")} data-testid="guest-form-name" /></div>
          <div className="col-span-2"><Label>Phone *</Label>
            <Input value={f.phone} onChange={(e) => { set("phone")(e); setCheck(null); }}
              onBlur={runCheck} placeholder="98765 43210 or +61 412 345 678"
              data-testid="guest-form-phone" />
            {ph && (
              <div className="mt-1 text-xs space-y-1" data-testid="guest-form-phone-check">
                {ph.status === "VALID" && <span className="text-emerald-700">✓ {ph.e164}</span>}
                {ph.status === "INVALID" && <span className="text-red-700">{ph.issues.join("; ")}</span>}
                {ph.status === "NEEDS_CONFIRMATION" && (
                  <div className="rounded border border-orange-300 bg-orange-50 p-2">
                    <div className="flex gap-1 text-orange-900"><AlertTriangle className="w-3.5 h-3.5" />
                      {ph.issues.join("; ")}</div>
                    <Label className="text-[11px]">Confirmed international number</Label>
                    <Input value={confirmE164} onChange={(e) => setConfirmE164(e.target.value)}
                      className="h-8" data-testid="guest-form-confirm-phone" />
                  </div>
                )}
                {ph.issues?.length > 0 && ph.status === "VALID" && (
                  <div className="text-amber-700">{ph.issues.join("; ")}</div>)}
                {check?.existing && (
                  <div className="text-red-700" data-testid="guest-form-duplicate-warning">
                    Already saved at this centre as “{check.existing.name}”.
                  </div>)}
              </div>
            )}
          </div>
          <div className="col-span-2"><Label>Email</Label>
            <Input value={f.email} onChange={set("email")} data-testid="guest-form-email" /></div>
          <div><Label>Birthday</Label>
            <Input type="date" value={f.birthday} onChange={set("birthday")} data-testid="guest-form-birthday" /></div>
          <div><Label>Anniversary</Label>
            <Input type="date" value={f.anniversary} onChange={set("anniversary")} data-testid="guest-form-anniversary" /></div>
          <div className="col-span-2"><Label>City / Area</Label>
            <Input value={f.city} onChange={set("city")} data-testid="guest-form-city" /></div>
          <div className="col-span-2"><Label>Notes</Label>
            <Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="guest-form-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="guest-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !f.name.trim() || !f.phone.trim()}
            data-testid="guest-form-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
